import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { pxToRem } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import Button from '../Button/Button';
import { ReactComponent as InfoGttIcon } from '../../assets/images/priceAlerts/info-gtt.svg';

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 2000;
`;

const Card = styled.div`
  background: ${themeColors.colorWhite};
  border-radius: ${variables.borderRadius.borderRadiusLg}
    ${variables.borderRadius.borderRadiusLg} 0 0;
  padding: ${pxToRem(24)};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${pxToRem(20)};
  box-shadow: 0 -12px 24px rgba(0, 0, 0, 0.12);
  animation: ${slideUp} 180ms ease-out;
`;

const Icon = styled(InfoGttIcon)`
  width: 35.75px;
  height: 35.75px;

  path {
    fill: ${themeColors.colorTextSecondary};
  }
`;

const Message = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
`;

const DismissButton = styled(Button)`
  width: 100%;

  && {
    font-size: 16px;
  }
`;

const PriceAlertInfoModal = ({ open, onClose, message }) => {
  useEffect(() => {
    if (!open) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  const { t } = useTranslation();

  if (!open) return null;

  return ReactDOM.createPortal(
    <Overlay onClick={onClose} data-cy="price-alert-info-modal">
      <Card onClick={(e) => e.stopPropagation()}>
        <Icon />
        <Message>{message}</Message>
        <DismissButton
          variant="primaryOutlined"
          height="44px"
          textTransform="none"
          onClick={onClose}
          data-cy="button-price-alert-dismiss"
        >
          {t('common.dismiss')}
        </DismissButton>
      </Card>
    </Overlay>,
    document.body,
  );
};

PriceAlertInfoModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  message: PropTypes.node,
};

export default PriceAlertInfoModal;
